import React, { useState } from 'react';
import { ChevronRight, ChevronLeft, Upload, FileText, X, CheckCircle, LayoutGrid } from './Icons';

const SetupForm = ({ onComplete, onCancel }) => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [config, setConfig] = useState({
    title: '',
    description: '',
    totalScore: 100,
    assignmentText: '',
    rubric: '',
    solution: '',
    selectedModels: ['gemini-2.5-pro'], // Always use gemini-2.5-pro internally
    useAverageGrading: false
  });

  const handleChange = (field, value) => {
    setConfig(prev => {
      const updated = { ...prev, [field]: value };
      // Clear file when text is entered
      if (field === 'assignmentText' && value.trim().length > 0) {
        updated.assignmentFile = undefined;
      } else if (field === 'rubric' && value.trim().length > 0) {
        updated.rubricFile = undefined;
      } else if (field === 'solution' && value.trim().length > 0) {
        updated.solutionFile = undefined;
      }
      return updated;
    });
  };

  const handleFileChange = async (field, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result.split(',')[1];
      setConfig(prev => {
        const updated = {
          ...prev,
          [field]: {
            mimeType: file.type,
            data: base64String,
            name: file.name
          }
        };
        // Clear text when file is uploaded
        if (field === 'assignmentFile') {
          updated.assignmentText = '';
        } else if (field === 'rubricFile') {
          updated.rubric = '';
        } else if (field === 'solutionFile') {
          updated.solution = '';
        }
        return updated;
      });
    };
    reader.readAsDataURL(file);
    if (e.target) e.target.value = '';
  };

  const removeFile = (field) => {
    setConfig(prev => ({ ...prev, [field]: undefined }));
  };

  const handleNext = async () => {
    if (step < 5) {
      setStep(step + 1);
    } else {
      // Step 5: Submit the assignment - navigation happens in parent
      try {
        // Set submitting state to disable button
        setIsSubmitting(true);
        
        // Call onComplete with config
        await onComplete(config);
      } catch (error) {
        console.error('❌ Error creating assignment:', error);
        alert(`Error creating assignment: ${error.message || 'Unknown error'}`);
        // Reset state on error
        setIsSubmitting(false);
      }
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
    else onCancel();
  };

  const isStepValid = () => {
    if (step === 1) return config.title.trim().length > 0 && config.description.trim().length > 0;
    if (step === 2) return !!config.assignmentFile || config.assignmentText.trim().length > 0;
    if (step === 3) return true;
    if (step === 4) return true;
    return true;
  };

  const PDFUploader = ({ field, currentFile, label, required = false, disabled = false }) => {
    const [isDragging, setIsDragging] = useState(false);

    const handleDragOver = (e) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    };

    const handleDragLeave = (e) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    };

    const handleDrop = (e) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        const file = files[0];
        if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
          handleFileChange(field, { target: { files: [file] } });
        }
      }
    };

    return (
      <div className="flex-1 flex flex-col min-h-0">
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
            {label} {required && <span className="text-red-500">*</span>}
          </label>
        </div>
        {currentFile ? (
          <div className="flex items-center justify-between p-2.5 bg-zinc-50 border border-zinc-200 rounded-lg">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-zinc-200 rounded flex items-center justify-center">
                <FileText className="w-3 h-3 text-zinc-600" />
              </div>
              <span className="text-sm font-medium text-zinc-900">{currentFile.name}</span>
            </div>
            <button onClick={() => removeFile(field)} className="p-1.5 hover:bg-zinc-200 rounded-full text-zinc-500 transition-colors">
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <label
            className={`group flex flex-col items-center justify-center w-full flex-1 border-2 border-dashed rounded-lg transition-all ${
              disabled
                ? 'cursor-not-allowed opacity-50 border-zinc-200'
                : isDragging
                ? 'cursor-pointer bg-zinc-100 border-zinc-900 border-solid'
                : 'cursor-pointer border-zinc-200 hover:bg-zinc-50 hover:border-zinc-300'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className={`flex flex-col items-center gap-2 transition-colors ${
              isDragging ? 'text-zinc-900' : 'text-zinc-400 group-hover:text-zinc-600'
            }`}>
              <Upload className={`w-6 h-6 ${isDragging ? 'animate-bounce' : ''}`} />
              <span className="text-base font-medium">Drag & drop PDF here</span>
              <span className="text-sm text-zinc-400">or click to browse</span>
            </div>
            <input type="file" className="hidden" accept=".pdf" onChange={(e) => !disabled && handleFileChange(field, e)} disabled={disabled} />
          </label>
        )}
      </div>
    );
  };

  const FileUploader = ({ field, currentFile, label, disabled = false }) => {
    const [isDragging, setIsDragging] = useState(false);

    const handleDragOver = (e) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    };

    const handleDragLeave = (e) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    };

    const handleDrop = (e) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        const file = files[0];
        if (file.type === 'application/pdf' || file.name.endsWith('.pdf') || file.name.endsWith('.txt')) {
          handleFileChange(field, { target: { files: [file] } });
        }
      }
    };

    return (
      <div className="flex-1 flex flex-col min-h-0">
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{label}</label>
        </div>
        {currentFile ? (
          <div className="flex items-center justify-between p-2.5 bg-zinc-50 border border-zinc-200 rounded-lg">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-zinc-200 rounded flex items-center justify-center">
                <FileText className="w-3 h-3 text-zinc-600" />
              </div>
              <span className="text-sm font-medium text-zinc-900">{currentFile.name}</span>
            </div>
            <button onClick={() => removeFile(field)} className="p-1.5 hover:bg-zinc-200 rounded-full text-zinc-500 transition-colors">
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <label
            className={`group flex flex-col items-center justify-center w-full flex-1 border-2 border-dashed rounded-lg transition-all ${
              disabled
                ? 'cursor-not-allowed opacity-50 border-zinc-200'
                : isDragging
                ? 'cursor-pointer bg-zinc-100 border-zinc-900 border-solid'
                : 'cursor-pointer border-zinc-200 hover:bg-zinc-50 hover:border-zinc-300'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className={`flex flex-col items-center gap-2 transition-colors ${
              isDragging ? 'text-zinc-900' : 'text-zinc-400 group-hover:text-zinc-600'
            }`}>
              <Upload className={`w-6 h-6 ${isDragging ? 'animate-bounce' : ''}`} />
              <span className="text-base font-medium">Drag & drop file here</span>
              <span className="text-sm text-zinc-400">or click to browse</span>
            </div>
            <input type="file" className="hidden" accept=".pdf,.txt" onChange={(e) => !disabled && handleFileChange(field, e)} disabled={disabled} />
          </label>
        )}
      </div>
    );
  };

  return (
    <div className="w-full h-full flex flex-col">
      <div className="w-full py-5 px-6 flex flex-col h-full">
        <div className="flex-shrink-0 mb-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-zinc-500 text-sm">Step {step} of 5</p>
            </div>
          </div>
          {/* Progress Bar */}
          <div className="relative mb-6">
            <div className="flex items-center justify-between mb-2 relative z-10">
              {[
                { num: 1, label: 'Details' },
                { num: 2, label: 'Questions' },
                { num: 3, label: 'Rubric' },
                { num: 4, label: 'Solution' },
                { num: 5, label: 'Review' }
              ].map((s) => (
                <div key={s.num} className="flex flex-col items-center gap-1.5 flex-1">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs transition-all duration-300 ${step === s.num ? 'bg-zinc-900 text-white shadow-md scale-110' :
                    step > s.num ? 'bg-zinc-900 text-white' :
                      'bg-zinc-100 text-zinc-400'
                    }`}>
                    {step > s.num ? <CheckCircle className="w-3.5 h-3.5" /> : s.num}
                  </div>
                  <span className={`text-xs font-semibold uppercase tracking-wider transition-colors duration-300 ${step >= s.num ? 'text-zinc-900' : 'text-zinc-400'
                    }`}>
                    {s.label}
                  </span>
                </div>
              ))}
            </div>
            <div className="absolute top-3.5 left-[12.5%] right-[12.5%] h-0.5 bg-zinc-100 -z-0">
              <div
                className="h-full bg-zinc-900 transition-all duration-500 ease-out"
                style={{ width: `${((step - 1) / 4) * 100}%` }}
              />
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0 flex flex-col">
          {/* Step 1: Details */}
          {step === 1 && (
            <div className="space-y-4 animate-in fade-in duration-300 h-full flex flex-col">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-zinc-700">Assignment Title <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={config.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  className="w-full bg-white border border-zinc-200 rounded-lg p-3 text-base font-medium focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-900 transition-all placeholder:text-zinc-300"
                  placeholder="Assignment 1"
                  autoFocus
                />
              </div>

              <div className="space-y-2 flex-1 flex flex-col min-h-0">
                <label className="text-sm font-semibold text-zinc-700">Instructions <span className="text-red-500">*</span></label>
                <textarea
                  value={config.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  className="w-full flex-1 bg-white border border-zinc-200 rounded-lg p-3 text-base focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-900 transition-all resize-none placeholder:text-zinc-300 leading-relaxed overflow-y-auto min-h-[200px]"
                  placeholder="Check the assignment strictly but fairly"
                />
              </div>
            </div>
          )}

          {/* Step 2: Questions */}
          {step === 2 && (
            <div className="space-y-3 animate-in fade-in duration-300 h-full flex flex-col">
              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Questions <span className="text-red-500">*</span></label>
              </div>
              <div className="flex flex-col gap-3 flex-1 min-h-0">
                <div className={`flex-[0.2] flex flex-col min-h-0 ${config.assignmentFile ? 'opacity-50 pointer-events-none' : ''}`}>
                  <textarea
                    value={config.assignmentText}
                    onChange={(e) => handleChange('assignmentText', e.target.value)}
                    disabled={!!config.assignmentFile}
                    className="w-full h-full bg-white border border-zinc-200 rounded-lg p-3 text-base focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-900 transition-all resize-none placeholder:text-zinc-300 overflow-y-auto disabled:cursor-not-allowed"
                    placeholder="Write the questions"
                    autoFocus
                  />
                </div>
                <div className="relative flex items-center justify-center py-1">
                  <div className="w-20 border-t border-zinc-100"></div>
                  <span className="flex-shrink-0 mx-3 text-xs text-zinc-400 font-mono">OR</span>
                  <div className="w-20 border-t border-zinc-100"></div>
                </div>
                <div className={`flex-[0.8] flex flex-col min-h-[200px] ${config.assignmentText.trim().length > 0 ? 'opacity-50 pointer-events-none' : ''}`}>
                  <PDFUploader field="assignmentFile" currentFile={config.assignmentFile} label="Assignment PDF" disabled={config.assignmentText.trim().length > 0} />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Rubric */}
          {step === 3 && (
            <div className="space-y-3 animate-in fade-in duration-300 h-full flex flex-col">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Rubric</label>
                  <span className="text-xs bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded font-mono">Optional</span>
                </div>
              </div>

              <div className="flex flex-col gap-3 flex-1 min-h-0">
                <div className={`flex-[0.2] flex flex-col min-h-0 ${config.rubricFile ? 'opacity-50 pointer-events-none' : ''}`}>
                  <textarea
                    value={config.rubric}
                    onChange={(e) => handleChange('rubric', e.target.value)}
                    disabled={!!config.rubricFile}
                    className="w-full h-full bg-white border border-zinc-200 rounded-lg p-3 text-base focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-900 transition-all resize-none placeholder:text-zinc-300 overflow-y-auto disabled:cursor-not-allowed"
                    placeholder="Write your evaluation criteria: total marks, marks for each subtask, etc."
                    autoFocus
                  />
                </div>
                <div className="relative flex items-center justify-center py-1">
                  <div className="w-20 border-t border-zinc-100"></div>
                  <span className="flex-shrink-0 mx-3 text-xs text-zinc-400 font-mono">OR</span>
                  <div className="w-20 border-t border-zinc-100"></div>
                </div>
                <div className={`flex-[0.8] flex flex-col min-h-[200px] ${config.rubric.trim().length > 0 ? 'opacity-50 pointer-events-none' : ''}`}>
                  <FileUploader field="rubricFile" currentFile={config.rubricFile} label="Upload Rubric" disabled={config.rubric.trim().length > 0} />
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Solution */}
          {step === 4 && (
            <div className="space-y-3 animate-in fade-in duration-300 h-full flex flex-col">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Solution</label>
                  <span className="text-xs bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded font-mono">Optional</span>
                </div>
              </div>
              <div className="flex flex-col gap-3 flex-1 min-h-0">
                <div className={`flex-[0.2] flex flex-col min-h-0 ${config.solutionFile ? 'opacity-50 pointer-events-none' : ''}`}>
                  <textarea
                    value={config.solution}
                    onChange={(e) => handleChange('solution', e.target.value)}
                    disabled={!!config.solutionFile}
                    className="w-full h-full bg-white border border-zinc-200 rounded-lg p-3 text-base focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-900 transition-all resize-none placeholder:text-zinc-300 overflow-y-auto disabled:cursor-not-allowed"
                    placeholder="Write the model answer"
                    autoFocus
                  />
                </div>
                <div className="relative flex items-center justify-center py-1">
                  <div className="w-20 border-t border-zinc-100"></div>
                  <span className="flex-shrink-0 mx-3 text-xs text-zinc-400 font-mono">OR</span>
                  <div className="w-20 border-t border-zinc-100"></div>
                </div>
                <div className={`flex-[0.8] flex flex-col min-h-[200px] ${config.solution.trim().length > 0 ? 'opacity-50 pointer-events-none' : ''}`}>
                  <FileUploader field="solutionFile" currentFile={config.solutionFile} label="Upload Solution" disabled={config.solution.trim().length > 0} />
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Review */}
          {step === 5 && (
            <div className="animate-in fade-in duration-300 h-full flex flex-col overflow-y-auto pr-1">
              <div className="bg-zinc-50 rounded-lg p-4 border border-zinc-200">
                <div className="flex items-center gap-2 mb-4">
                  <div className="bg-zinc-900 p-1.5 rounded-md">
                    <LayoutGrid className="w-3 h-3 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-base text-zinc-900">Summary</h3>
                    <p className="text-xs text-zinc-500">Review configuration</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-1">Title</h4>
                    <div className="text-base font-medium text-zinc-900 truncate">{config.title}</div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-2.5 bg-white border border-zinc-200 rounded-lg">
                      <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-1">Questions</h4>
                      <div className="flex items-center gap-1.5">
                        {config.assignmentFile ? <FileText className="w-3 h-3 text-zinc-500" /> : <FileText className="w-3 h-3 text-zinc-500" />}
                        <span className="text-sm text-zinc-600 truncate">
                          {config.assignmentFile ? 'PDF Uploaded' : (config.assignmentText ? 'Text Provided' : 'None')}
                        </span>
                      </div>
                    </div>
                    <div className="p-2.5 bg-white border border-zinc-200 rounded-lg">
                      <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-1">Rubric</h4>
                      <div className="flex items-center gap-1.5">
                        {config.rubricFile ? <FileText className="w-3 h-3 text-zinc-500" /> : <FileText className="w-3 h-3 text-zinc-500" />}
                        <span className="text-sm text-zinc-600 truncate">
                          {config.rubricFile ? 'PDF Uploaded' : (config.rubric ? 'Text Provided' : 'None')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex justify-between items-center pt-4 border-t border-zinc-100 mt-3">
          <button
            onClick={handleBack}
            className="flex items-center gap-1.5 px-6 py-2.5 rounded-lg font-semibold transition-all duration-300 group text-xs border bg-white text-zinc-900 border-zinc-200 hover:bg-gradient-to-r hover:from-zinc-900 hover:to-zinc-800 hover:text-white hover:border-zinc-900 hover:shadow-lg hover:scale-105"
          >
            <ChevronLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" />
            {step === 1 ? 'Cancel' : 'Back'}
          </button>
          <button
            onClick={handleNext}
            disabled={!isStepValid() || isSubmitting}
            className={`flex items-center gap-1.5 px-6 py-2.5 rounded-lg font-semibold transition-all duration-300 group text-xs border ${
              isStepValid() && !isSubmitting
                ? 'bg-white text-zinc-900 border-zinc-200 hover:bg-gradient-to-r hover:from-zinc-900 hover:to-zinc-800 hover:text-white hover:border-zinc-900 hover:shadow-lg hover:scale-105'
                : 'bg-white text-zinc-900 border-zinc-200 cursor-not-allowed'
            }`}
          >
            {isSubmitting ? (
              <>
                <div className="w-3 h-3 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" />
                Creating...
              </>
            ) : (
              <>
                {step === 5 ? 'Create Assignment' : 'Continue'}
                <ChevronRight className={`w-3 h-3 ${isStepValid() ? 'group-hover:translate-x-1' : ''} transition-transform`} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SetupForm;
